import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {Box, BoxProps, Divider, List, ListItemText, Theme, Tooltip} from '@mui/material';
import {ReactElement, ReactNode, createElement, forwardRef} from 'react';
import {useLocation} from 'react-router';
import {Translation} from '../i18n/Translation';
import {ComponentWithoutChildren, PropsWithoutChildren} from '../react/types';
import {ListItemRouteButton} from './ListItemRouteButton';
import {NavigationMenuEntry, NavigationMenuSubEntry} from './types';

type MenuOrientation = 'horizontal' | 'vertical';

type AllowedBoxProps = Omit<BoxProps, 'ref'>;
export interface NavigationMenuProps extends PropsWithoutChildren<AllowedBoxProps> {
  closeMenu?: () => void;
  menuItems: NavigationMenuEntry[][];
  orientation?: MenuOrientation;
  renderText?: boolean;
}

export const NavigationMenu = forwardRef<HTMLElement, NavigationMenuProps>(
  ({closeMenu, menuItems, orientation = 'vertical', renderText = true, sx, ...boxProps}: NavigationMenuProps, ref) => {
    return (
      <Box {...boxProps} ref={ref} sx={{width: '100%', ...sx}}>
        {menuItems.map((items, sectionIndex) => (
          <div key={`menu-section-${sectionIndex}`}>
            <List disablePadding>
              {items.map((item, itemIndex) => (
                <Box key={`menu-section-${sectionIndex}-item-${itemIndex}`}>
                  <NavigationMenuItem
                    {...item}
                    closeMenu={closeMenu}
                    orientation={orientation}
                    renderText={renderText}
                    variant="entry"
                  />
                  {item.subEntries?.map((sub, subIndex) => (
                    <NavigationMenuItem
                      closeMenu={closeMenu}
                      iconType={SubdirectoryArrowRightIcon}
                      key={`menu-section-${sectionIndex}-item-${itemIndex}-sub-${subIndex}`}
                      {...sub}
                      orientation={orientation}
                      renderText={renderText}
                      variant="subentry"
                    />
                  ))}
                </Box>
              ))}
            </List>
            {sectionIndex < menuItems.length - 1 && (
              <Divider orientation="horizontal" sx={{mb: 1, mt: 1}} variant="middle" />
            )}
          </div>
        ))}
      </Box>
    );
  },
);
NavigationMenu.displayName = 'NavigationMenu';

interface NavigationMenuItemProps extends PropsWithoutChildren<NavigationMenuEntry> {
  closeMenu?: () => void;
  orientation: MenuOrientation;
  renderText: boolean;
  subEntries?: NavigationMenuSubEntry[];
  variant: 'entry' | 'subentry';
}
const NavigationMenuItem: ComponentWithoutChildren<NavigationMenuItemProps> = ({
  closeMenu,
  iconType,
  orientation,
  path,
  translationKey,
  renderText,
  variant,
}: NavigationMenuItemProps) => {
  const {pathname} = useLocation();
  const isCurrentPath = pathname.startsWith(path);
  return (
    <Translation keys={translationKey}>
      {(text) => {
        const icon =
          (variant === 'entry' || orientation === 'horizontal') &&
          maybeAddTooltip(createElement(iconType, {color: 'action'}), text, renderText);
        return (
          <ListItemRouteButton
            afterNavigation={closeMenu}
            aria-label={text}
            sx={{backgroundColor: (theme: Theme) => (isCurrentPath ? theme.palette.action.focus : null)}}
            to={path}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: orientation === 'vertical' ? 'column' : 'row',
                justifyContent: orientation === 'vertical' ? 'center' : 'flex-start',
                minHeight: 4,
                ml: variant === 'subentry' ? 1 : 0,
                width: '100%',
              }}
            >
              {orientation === 'horizontal' ? (
                <Box sx={{alignItems: 'center', display: 'flex', flexDirection: 'row', mr: renderText ? 2 : 0}}>
                  {icon}
                </Box>
              ) : (
                icon
              )}
              {renderText && variant === 'entry' && <ListItemText primary={text} />}
              {variant === 'subentry' && <ListItemText secondary={text} />}
            </Box>
          </ListItemRouteButton>
        );
      }}
    </Translation>
  );
};

function maybeAddTooltip(node: ReactElement<unknown>, title: string, renderText?: boolean): ReactNode {
  if (renderText) {
    return node;
  }
  return <Tooltip title={title}>{node}</Tooltip>;
}
